// quiron/frontend/src/components/ConsultationList.js

import React, { useEffect, useState, useCallback } from 'react';
import { getAllConsultations, deleteConsultations as apiDeleteConsultations } from '../api';
import axios from 'axios';
import styles from './ConsultationManager.module.css';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ConsultationDetailModal from './ConsultationDetailModal';
import InlineLoading from './InlineLoading';

function ConsultationList() {
  const { t } = useTranslation();
  const [consultations, setConsultations] = useState([]);
  const [filteredConsultations, setFilteredConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // const [expandedId, setExpandedId] = useState(null); // <-- REMOVER ESTA LINHA
  const [isModalOpen, setIsModalOpen] = useState(false); // <-- ADICIONAR
  const [selectedConsultation, setSelectedConsultation] = useState(null); // <-- ADICIONAR

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchConsultations = useCallback(async (controllerSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllConsultations({ signal: controllerSignal });
      const sortedData = (data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setConsultations(sortedData);
      setFilteredConsultations(sortedData);
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Erro detalhado ao buscar consultas:', err);
        setError(t('serverCommunicationError'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const controller = new AbortController();
    fetchConsultations(controller.signal);
    return () => controller.abort();
  }, [fetchConsultations]);

  useEffect(() => {
    const lowercasedTerm = searchTerm.toLowerCase();
    const filtered = consultations.filter((consultation) =>
      Object.values(consultation).some((value) => {
        if (value === null || typeof value === 'undefined') return false;
        if (typeof value === 'boolean') {
          return (value ? "primeira" : "retorno").includes(lowercasedTerm);
        }
        if (value instanceof Date || (typeof value === 'string' && !isNaN(new Date(value).getTime()))) {
          return new Date(value).toLocaleString('pt-BR').toLowerCase().includes(lowercasedTerm);
        }
        return String(value).toLowerCase().includes(lowercasedTerm);
      })
    );
    setFilteredConsultations(filtered);
  }, [searchTerm, consultations]);

  const handleExpandClick = (consultation) => {
    setSelectedConsultation(consultation);
    setIsModalOpen(true);
  };

  const handleSelectRow = (id) => {
    setSelectedIds(prevSelectedIds => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (newSelectedIds.has(id)) {
        newSelectedIds.delete(id);
      } else {
        newSelectedIds.add(id);
      }
      return newSelectedIds;
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allVisibleIds = new Set(filteredConsultations.map(c => c.id));
      setSelectedIds(allVisibleIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) {
      alert(t('noConsultationsSelectedForDeletion'));
      return;
    }
    if (window.confirm(t('confirmDeleteConsultations', { count: selectedIds.size }))) {
      setIsDeleting(true);
      try {
        await apiDeleteConsultations(Array.from(selectedIds));
        setConsultations(prev => prev.filter(c => !selectedIds.has(c.id)));
        setFilteredConsultations(prev => prev.filter(c => !selectedIds.has(c.id)));
        setSelectedIds(new Set());
        alert(t('consultationsDeletedSuccess'));
      } catch (err) {
        console.error('Erro ao excluir consultas:', err);
        alert(t('errorDeletingConsultations', { message: err.message || t('unknownError') }));
      } finally {
        setIsDeleting(false);
      }
    }
  };
  
  const isAllSelected = filteredConsultations.length > 0 && selectedIds.size === filteredConsultations.length;

  return (
    <div className={styles.listSectionCard}>
      <div className={styles.listHeader}>
        <h3 className={styles.cardTitle}>{t('savedConsultations')}</h3>
        {selectedIds.size > 0 && (
          <button
            onClick={handleDeleteSelected}
            className={styles.deleteSelectedButton}
            disabled={isDeleting}
          >
            <FontAwesomeIcon icon={faTrash} /> {isDeleting ? t('deleting') : t('deleteSelected', { count: selectedIds.size })}
          </button>
        )}
      </div>
      <input
        type="text"
        placeholder={t('searchAllColumns')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className={styles.searchInput}
      />
      {loading && <InlineLoading text={t('loadingConsultations')} />}
      {error && <p style={{ color: 'var(--notification-error-bg)' }}>{error}</p>}
      {!loading && !error && filteredConsultations.length === 0 && (
        <p>{t('noConsultationsFound')}</p>
      )}
      {!loading && !error && filteredConsultations.length > 0 && (
        <div className={styles.tableContainer}>
          <table className={styles.consultationListTable}>
            <thead className={styles.consultationListThead}>
              <tr>
                <th className={styles.consultationListThCheckbox}>
                  <label className={styles.customCheckboxContainer} htmlFor="selectAllCheckbox">
                    <input
                      type="checkbox"
                      id="selectAllCheckbox"
                      onChange={handleSelectAll}
                      checked={isAllSelected}
                      className={styles.hiddenCheckboxInput}
                    />
                    <span className={styles.customCheckboxStyled}></span>
                  </label>
                </th>
                <th className={styles.consultationListTh}>{t('id')}</th>
                <th className={styles.consultationListTh}>{t('specialty')}</th>
                <th className={styles.consultationListTh}>{t('type')}</th>
                <th className={styles.consultationListTh}>{t('originalConsultation')}</th>
                <th className={styles.consultationListTh}>{t('improvedConsultation')}</th>
                <th className={styles.consultationListTh}>{t('summary')}</th>
                <th className={styles.consultationListTh}>{t('date')}</th>
                <th className={styles.consultationListTh}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredConsultations.map((consultation) => (
                <React.Fragment key={consultation.id}>
                  <tr className={`${styles.consultationListTr} ${selectedIds.has(consultation.id) ? styles.selectedRow : ''}`}>
                    <td className={styles.consultationListTdCheckbox}>
                      <label className={styles.customCheckboxContainer} htmlFor={`selectRowCheckbox-${consultation.id}`}>
                        <input
                          type="checkbox"
                          id={`selectRowCheckbox-${consultation.id}`}
                          checked={selectedIds.has(consultation.id)}
                          onChange={() => handleSelectRow(consultation.id)}
                          className={styles.hiddenCheckboxInput}
                        />
                        <span className={styles.customCheckboxStyled}></span>
                      </label>
                    </td>
                    <td className={styles.consultationListTd}>{consultation.id}</td>
                    <td className={styles.consultationListTd}>{consultation.specialty}</td>
                    <td className={styles.consultationListTd}>
                      {consultation.is_first_consultation ? t('first') : t('return')}
                    </td>
                    <td className={styles.consultationListTdPreview}>
                      <div className={styles.previewContent}>
                        <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                          {(consultation.raw_notes || '—').substring(0, 100) + (consultation.raw_notes && consultation.raw_notes.length > 100 ? '...' : '')}
                        </ReactMarkdown>
                      </div>
                    </td>
                    <td className={styles.consultationListTdPreview}>
                      <div className={styles.previewContent}>
                        <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                          {(consultation.improved_notes || '—').substring(0, 100) + (consultation.improved_notes && consultation.improved_notes.length > 100 ? '...' : '')}
                        </ReactMarkdown>
                      </div>
                    </td>
                    <td className={styles.consultationListTdPreview}>
                      <div className={styles.previewContent}>
                        <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                          {(consultation.summary || '—').substring(0, 100) + (consultation.summary && consultation.summary.length > 100 ? '...' : '')}
                        </ReactMarkdown>
                      </div>
                    </td>
                    <td className={styles.consultationListTd}>
                      {new Date(consultation.created_at).toLocaleString('pt-BR')}
                    </td>
                    <td className={styles.consultationListTd}>
                      <button
                        className={styles.expandButton}
                        onClick={() => handleExpandClick(consultation)} // ATUALIZAR O HANDLER
                      >
                        {t('expand')} {/* Não precisamos mais alternar o texto */}
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConsultationDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        consultation={selectedConsultation}
      />
    </div>
  );
}

export default ConsultationList;